import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, CreditCard, AlertTriangle } from 'lucide-react';
import { useLang, LangToggle } from '@/lib/i18n';

/**
 * `/start` — a patient whose doctor is not on DoctorPaperwork starts here
 * (ADR 0008). Route first, intake later: this form asks only what it takes to
 * reach the office and the patient (decision 2). The leave details are filled
 * after a practice accepts, through the ordinary magic link.
 *
 * The fee shown comes from the server; the server ignores any amount we send.
 */
export default function PatientRequestStart() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    patient_name: '', patient_email: '', patient_phone: '', sms_consent: false,
    doctor_name: '', office_email: '',
  });

  useEffect(() => {
    let alive = true;
    api.get('/patient-requests/config')
      .then((r) => { if (alive) setConfig(r.data); })
      .catch(() => { if (alive) setConfig({ enabled: false }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (params.get('canceled')) toast.message(t('pd.canceled'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, sms_consent: !!(form.patient_phone && form.sms_consent), language: lang };
    try {
      let r;
      try {
        r = await api.post('/patient-requests', payload);
      } catch (err) {
        // The demo has no Stripe account behind it: fall back to the demo-only
        // mock hold, which the server allows only with ALLOW_MOCK_PAY in demo mode.
        if (err?.response?.status === 503 && config?.mock_pay) {
          r = await api.post('/patient-requests', { ...payload, mock_pay: true });
        } else {
          throw err;
        }
      }
      window.location.assign(r.data.url);
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-wash grid place-items-center p-4 sm:p-6">
      <Card className="w-full max-w-lg" data-testid="pd-start-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-lg">{t('pd.startTitle')}</CardTitle>
            <LangToggle />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!config ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('layout.loading')}
            </div>
          ) : !config.enabled ? (
            <div className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="pd-unavailable">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('pd.unavailable')}</span>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-muted-foreground">{t('pd.startSubtitle')}</p>

              <div className="space-y-1.5">
                <Label htmlFor="pd-name">{t('pd.yourName')}</Label>
                <Input id="pd-name" required autoComplete="name" value={form.patient_name} onChange={set('patient_name')} data-testid="pd-patient-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pd-email">{t('pd.yourEmail')}</Label>
                <Input id="pd-email" type="email" required autoComplete="email" value={form.patient_email} onChange={set('patient_email')} data-testid="pd-patient-email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pd-phone">{t('pd.yourPhone')}</Label>
                <Input id="pd-phone" type="tel" autoComplete="tel" value={form.patient_phone} onChange={set('patient_phone')} data-testid="pd-patient-phone" />
                {form.patient_phone.trim() && (
                  <label className="flex items-start gap-2 text-xs text-muted-foreground pt-1">
                    <Checkbox checked={form.sms_consent} onCheckedChange={(v) => setForm((f) => ({ ...f, sms_consent: v === true }))} data-testid="pd-sms-consent" />
                    <span>{t('pd.smsConsent')}</span>
                  </label>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pd-doctor">{t('pd.doctorName')}</Label>
                <Input id="pd-doctor" required value={form.doctor_name} onChange={set('doctor_name')} data-testid="pd-doctor-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pd-office">{t('pd.officeEmail')}</Label>
                <Input id="pd-office" type="email" required value={form.office_email} onChange={set('office_email')} data-testid="pd-office-email" />
                <p className="text-xs text-muted-foreground">{t('pd.officeEmailHint')}</p>
              </div>

              <div className="rounded-lg bg-secondary p-3 text-sm space-y-1" data-testid="pd-fee">
                <p className="font-semibold flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" /> {t('pd.feeTitle', { fee: Number(config.fee).toFixed(2) })}
                </p>
                <p className="text-muted-foreground text-xs">{t('pd.feeBody', { days: config.hold_days })}</p>
              </div>
              <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span>{t('pd.privacy')}</span>
              </div>

              <Button type="submit" className="w-full h-11" disabled={busy} data-testid="pd-submit">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {busy ? t('pd.starting') : t('pd.continue')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
